import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertCircle, Loader2 } from "lucide-react";
import menuService from "@/services/menuService";
import profileService from "@/services/profileService";
import userService from "@/services/userService";
import { showError } from "@/store/errorStore";

/**
 * Erros HTTP já são exibidos pelo modal global (axiosInterceptor);
 * só mostramos aqui erros sem resposta do servidor (ex.: falha local).
 */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Erro");
  }
};

/**
 * Matriz de permissões: marca/desmarca quais perfis acessam cada menu.
 * Cada clique salva na hora via PUT /profiles/{id} (campo menus).
 */
export default function PermissionsMatrix({
  profiles = [],
  menus = [],
  currentUser,
  isOwner,
  loading,
  onReload,
}) {
  const [pendingCell, setPendingCell] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const hasAccess = (profile, menu) =>
    (profile.menus || []).includes(menu.link);

  const profilesWithMenu = (menu) =>
    profiles.filter((profile) => hasAccess(profile, menu));

  // Permissões (PUT /profiles/{id}) são exclusivas do ROLE_OWNER
  const canEditProfile = () => !!isOwner;

  /**
   * Salva a permissão do perfil para o menu
   */
  const savePermission = async (profile, menu, grant) => {
    const key = `${profile.id}:${menu.link}`;
    setPendingCell(key);
    try {
      const current = profile.menus || [];
      const nextMenus = grant
        ? [...current, menu.link]
        : current.filter((link) => link !== menu.link);

      await profileService.updateProfile(profile.id, {
        name: profile.name,
        menus: nextMenus,
      });
      await onReload();
      menuService.notifyMenusChanged();
    } catch (error) {
      showLocalError(error, "Erro ao salvar permissão");
    } finally {
      setPendingCell(null);
    }
  };

  /**
   * Avisos antes de remover acesso:
   * - menu sem nenhum perfil fica visível para todos os usuários;
   * - o usuário logado pode perder o acesso ao próprio menu.
   */
  const handleToggle = (profile, menu, grant) => {
    if (grant) {
      savePermission(profile, menu, true);
      return;
    }

    const remaining = profilesWithMenu(menu).filter((p) => p.id !== profile.id);
    const warnings = [];

    if (remaining.length === 0) {
      warnings.push(
        `O menu "${menu.label}" ficará sem perfis vinculados e passará a ser visível para TODOS os usuários.`,
      );
    } else {
      const userKeepsAccess = remaining.some((p) =>
        userService.hasRole(currentUser, p.name),
      );
      if (userService.hasRole(currentUser, profile.name) && !userKeepsAccess) {
        warnings.push(
          `Você deixará de ver o menu "${menu.label}", pois nenhum outro perfil seu tem acesso a ele.`,
        );
      }
    }

    if (warnings.length > 0) {
      setConfirmation({ profile, menu, warnings });
    } else {
      savePermission(profile, menu, false);
    }
  };

  const handleConfirm = () => {
    if (confirmation) {
      savePermission(confirmation.profile, confirmation.menu, false);
    }
    setConfirmation(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Permissões por Perfil</CardTitle>
          <CardDescription>
            Marque os menus que cada perfil pode acessar. As alterações são
            salvas automaticamente. Menu sem nenhum perfil marcado fica visível
            para todos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando permissões...</p>
              </div>
            </div>
          ) : menus.length === 0 || profiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {menus.length === 0
                  ? "Nenhum menu cadastrado"
                  : "Nenhum perfil cadastrado"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Menu</TableHead>
                    {profiles.map((profile) => (
                      <TableHead
                        key={profile.id}
                        className="text-center min-w-[110px]"
                      >
                        <div className="font-medium text-gray-900">
                          {profileService.getProfileLabel(profile.name)}
                        </div>
                        <div className="font-mono text-[10px] text-gray-400">
                          {profile.name}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {menus.map((menu) => {
                    const IconComponent = menuService.resolveIcon(menu.icon);
                    const visibleToAll = profilesWithMenu(menu).length === 0;
                    return (
                      <TableRow key={menu.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 font-medium">
                            <IconComponent className="w-4 h-4 text-gray-600" />
                            {menu.label}
                          </div>
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {visibleToAll && (
                              <Badge
                                variant="outline"
                                className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
                              >
                                Visível para todos
                              </Badge>
                            )}
                            {menu.enabled === false && (
                              <Badge
                                variant="outline"
                                className="bg-gray-100 text-gray-600 border-gray-200 text-[10px]"
                              >
                                Inativo
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {profiles.map((profile) => {
                          const key = `${profile.id}:${menu.link}`;
                          const isPending = pendingCell === key;
                          const editable = canEditProfile(profile);
                          return (
                            <TableCell key={key} className="text-center">
                              {isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-500" />
                              ) : (
                                <Checkbox
                                  checked={hasAccess(profile, menu)}
                                  onCheckedChange={(checked) =>
                                    handleToggle(
                                      profile,
                                      menu,
                                      checked === true,
                                    )
                                  }
                                  disabled={!editable || pendingCell !== null}
                                  title={
                                    editable
                                      ? undefined
                                      : "Apenas proprietários podem alterar este perfil"
                                  }
                                  aria-label={`${profileService.getProfileLabel(
                                    profile.name,
                                  )} acessa ${menu.label}`}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!confirmation}
        onOpenChange={(open) => !open && setConfirmation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar remoção de acesso</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {confirmation?.warnings.map((warning) => (
                  <p key={warning}>{warning}</p>
                ))}
                <p>Deseja continuar?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Remover acesso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
